/**
 * PortfolioByLevelCards Component
 * Displays 4 color-coded cards for BRONZE, SILVER, GOLD, PLATINUM credit levels
 * Shows credit count per level from portfolioByLevel record
 */

import { CreditLevel } from '@/types/credit.types';
import { cn } from '@/lib/utils';
import { Award, Medal, Crown, Gem } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// ─── Level Configuration ─────────────────────────────────────────────────────

interface LevelConfig {
  label: string;
  icon: LucideIcon;
  bgClass: string;
  borderClass: string;
  iconClass: string;
  textClass: string;
}

const LEVEL_CONFIG: Record<CreditLevel, LevelConfig> = {
  [CreditLevel.BRONZE]: {
    label: 'Bronze',
    icon: Medal,
    bgClass: 'bg-amber-50 dark:bg-amber-950/30',
    borderClass: 'border-amber-200 dark:border-amber-800',
    iconClass: 'text-amber-600 dark:text-amber-400',
    textClass: 'text-amber-700 dark:text-amber-300',
  },
  [CreditLevel.SILVER]: {
    label: 'Silver',
    icon: Award,
    bgClass: 'bg-slate-50 dark:bg-slate-950/30',
    borderClass: 'border-slate-300 dark:border-slate-700',
    iconClass: 'text-slate-500 dark:text-slate-400',
    textClass: 'text-slate-700 dark:text-slate-300',
  },
  [CreditLevel.GOLD]: {
    label: 'Gold',
    icon: Crown,
    bgClass: 'bg-yellow-50 dark:bg-yellow-950/30',
    borderClass: 'border-yellow-300 dark:border-yellow-700',
    iconClass: 'text-yellow-600 dark:text-yellow-400',
    textClass: 'text-yellow-700 dark:text-yellow-300',
  },
  [CreditLevel.PLATINUM]: {
    label: 'Platinum',
    icon: Gem,
    bgClass: 'bg-indigo-50 dark:bg-indigo-950/30',
    borderClass: 'border-indigo-200 dark:border-indigo-800',
    iconClass: 'text-indigo-600 dark:text-indigo-400',
    textClass: 'text-indigo-700 dark:text-indigo-300',
  },
};

const LEVELS_ORDER: CreditLevel[] = [
  CreditLevel.BRONZE,
  CreditLevel.SILVER,
  CreditLevel.GOLD,
  CreditLevel.PLATINUM,
];

// ─── Component ───────────────────────────────────────────────────────────────

interface PortfolioByLevelCardsProps {
  data: Record<string, number>;
  totalCredits: number;
}

export function PortfolioByLevelCards({ data, totalCredits }: PortfolioByLevelCardsProps) {
  const safeData = data ?? {};

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {LEVELS_ORDER.map((level) => {
        const config = LEVEL_CONFIG[level];
        const count = safeData[level] ?? 0;
        const Icon = config.icon;
        const pct = totalCredits > 0 ? ((count / totalCredits) * 100).toFixed(1) : '0.0';

        return (
          <div
            key={level}
            className={cn(
              'rounded-lg border p-4 transition-shadow hover:shadow-sm',
              config.bgClass,
              config.borderClass,
            )}
          >
            <div className="flex items-center gap-2">
              <Icon size={18} className={config.iconClass} />
              <span className={cn('text-sm font-semibold', config.textClass)}>
                {config.label}
              </span>
            </div>
            <div className="mt-3">
              <span className={cn('text-2xl font-bold tabular-nums', config.textClass)}>
                {count}
              </span>
            </div>
            <span className="mt-1 block text-xs text-muted-foreground">
              {pct}% del portafolio
            </span>
          </div>
        );
      })}
    </div>
  );
}
